import { DEFAULT_PAGE_SIZE } from '@/lib/constants/pagination';
import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { axiosInstance } from '@/utils/axios';

import { mapInvoiceDetailsFromResponse } from '../mappers/invoice-details.mapper';
import { mapInvoiceSummariesFromResponse } from '../mappers/invoice-summary.mapper';
import { GetInvoicesFilter, InvoiceDetails, InvoiceListItem } from '../types';
import {
  CreateInvoicesRequest,
  DeleteInvoiceRequest,
  FindInvoiceSummariesRequest,
  GetCreationJobStatusRequest,
  UpdateInvoiceRequest,
} from '../types/dto/request';
import {
  CreateInvoicesResponse,
  CreationJobStatusResponse,
  InvoiceDetailsResponse,
  InvoiceSummaryResponse,
  UpdateInvoiceResponse,
} from '../types/dto/response';

interface GetInvoicesParams {
  cursor?: number;
  filters: GetInvoicesFilter;
  /**
   * Override the page size. Defaults to DEFAULT_PAGE_SIZE. Used by the polling
   * fallback to reload all currently-loaded items in a single request.
   */
  pageSize?: number;
}

/**
 * Retrieves paginated list of invoices with filters and search.
 * POST /api/invoices/search
 *
 * @param params - Cursor and filters for the invoices search (startDate and endDate are required)
 * @returns Paginated list of invoice list items
 */
export const getInvoices = async ({
  cursor,
  filters,
  pageSize = DEFAULT_PAGE_SIZE,
}: GetInvoicesParams): Promise<PaginatedResponse<InvoiceListItem>> => {
  const endpoint = '/api/invoices/search';

  const requestBody: FindInvoiceSummariesRequest = {
    ...filters,
    pagination: {
      pageSize,
      cursor: cursor,
    },
  };

  const apiData = await withApiErrorHandler(endpoint, ApiErrorCode.INVOICES_LOAD_FAILED, () =>
    axiosInstance.post<{
      data: {
        items: InvoiceSummaryResponse[];
        pagination: {
          total: number;
          cursor: number | null;
        };
      };
      error?: { status: number; message: string; code: string };
    }>(endpoint, requestBody)
  );

  return new PaginatedResponse({
    items: mapInvoiceSummariesFromResponse(apiData.items || []),
    pagination: apiData.pagination || { total: 0, cursor: null },
  });
};

/** Retrieves one invoice's read-only details. GET /api/invoices/:invoiceId */
export const getInvoiceDetails = async (invoiceId: number): Promise<InvoiceDetails> => {
  const endpoint = `/api/invoices/${invoiceId}`;

  const apiData = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.INVOICE_DETAILS_LOAD_FAILED,
    () =>
      axiosInstance.get<{
        data: InvoiceDetailsResponse;
        error?: { status: number; message: string; code: string };
      }>(endpoint)
  );

  return mapInvoiceDetailsFromResponse(apiData);
};

/**
 * Creates new invoices for the organization.
 * POST /api/invoices
 *
 * @param request - Invoice creation data
 * @returns Job ID for tracking invoice creation status
 */
export const createInvoices = async (
  request: CreateInvoicesRequest
): Promise<CreateInvoicesResponse> => {
  const endpoint = '/api/invoices';

  return await withApiErrorHandler(endpoint, ApiErrorCode.INVOICES_CREATE_FAILED, () =>
    axiosInstance.post<{
      data: CreateInvoicesResponse;
      error?: { status: number; message: string; code: string };
    }>(endpoint, request)
  );
};

/**
 * Deletes an invoice by ID.
 * DELETE /api/invoices
 *
 * @param params - Invoice ID to delete
 */
export const deleteInvoice = async (params: DeleteInvoiceRequest): Promise<void> => {
  const endpoint = '/api/invoices';

  await withApiErrorHandler(endpoint, ApiErrorCode.INVOICE_DELETE_FAILED, () =>
    axiosInstance.delete(endpoint, {
      params: {
        invoiceId: params.invoiceId,
      },
    })
  );
};

/**
 * Retrieves the status of an invoice creation job.
 * GET /api/invoices/creation-job-status
 *
 * @param params - Job ID to check status for
 * @returns Job status (PENDING, EXECUTING, COMPLETED, FAILED)
 */
export const getCreationJobStatus = async (
  params: GetCreationJobStatusRequest
): Promise<CreationJobStatusResponse> => {
  const endpoint = '/api/invoices/creation-job-status';

  return await withApiErrorHandler(endpoint, ApiErrorCode.INVOICE_JOB_STATUS_LOAD_FAILED, () =>
    axiosInstance.get<{
      data: CreationJobStatusResponse;
      error?: { status: number; message: string; code: string };
    }>(endpoint, {
      params: {
        jobId: params.jobId,
      },
    })
  );
};

/** Submits a full invoice replacement and returns its asynchronous job ID. */
export const updateInvoice = async ({
  invoiceId,
  request,
}: {
  invoiceId: number;
  request: UpdateInvoiceRequest;
}): Promise<UpdateInvoiceResponse> => {
  const endpoint = `/api/invoices/${invoiceId}`;

  return await withApiErrorHandler(endpoint, ApiErrorCode.INVOICE_UPDATE_FAILED, () =>
    axiosInstance.patch<{
      data: UpdateInvoiceResponse;
      error?: { status: number; message: string; code: string };
    }>(endpoint, request)
  );
};

/**
 * Retries an invoice that failed to generate.
 * POST /api/invoices/:invoiceId/retry
 *
 * @param invoiceId - ID of the invoice to retry
 */
export const retryInvoice = async (invoiceId: number): Promise<void> => {
  const endpoint = `/api/invoices/${invoiceId}/retry`;

  await withApiErrorHandler(endpoint, ApiErrorCode.INVOICE_RETRY_FAILED, () =>
    axiosInstance.post(endpoint)
  );
};
